import React from 'react';
import { withRouteData } from 'react-static';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Cookies from 'js-cookie';
import Hyperlink from '../../common/Hyperlink';
import { media } from '../../../styles/style-utils';
import arrowRightIcon from '../../../assets/images/arrow-right-circle-filled.svg';
import { COOKIE_PREFERENCES_COOKIE, BANNER_COOKIE, GIFT_CARD_PATH } from '../../../constants';
import { campaignPaths as allCampaignPaths } from './campaignBannerConfig';
import { type } from '../../../styles/typography';

const Banner = styled(Hyperlink)`
  ${type('label', 'small', { uppercase: true })};
  background-color: var(--c-yellow);
  color: var(--b-black);
  width: 100%;
  min-height: 40px;
  padding: 10px var(--b-gutter);
  display: flex;
  justify-content: center;
  align-items: center;
  text-decoration: none;

  ${media.large`
    padding: 10px calc(var(--fr) * 3);
  `}
`;

const BannerText = styled.span`
  .titleMini {
    ${media.small`display: none;`}
  }

  .titleSmall {
    display: none;
    ${media.small`display: inline-block;`}
    ${media.medium`display: none`}
  }

  .titleMedium {
    display: none;
    ${media.medium`display: inline-block;`}
  }

  .highlight {
    font-weight: 600;
  }
`;

const BannerIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-left: 0.5rem;
  filter: invert(1);
`;

const getCookieConfig = () => ({
  expires: 365 * 100,
  domain: process.env.TOP_DOMAIN,
});

const isBlacklistPath = path =>
  allCampaignPaths.some(campaignPath => path === campaignPath) ||
  path.includes('magazine') ||
  path.includes(GIFT_CARD_PATH);

const hasCookiePreferences = Boolean(Cookies.get(COOKIE_PREFERENCES_COOKIE));

const getBannerCookie = campaignPath => {
  const cookie = Cookies.get(BANNER_COOKIE);
  if (cookie) {
    const parsedCookie = JSON.parse(cookie);
    return parsedCookie[campaignPath];
  }
  return null;
};

const setBannerCookie = (campaignPath, seen = true, clicked = false) => {
  Cookies.set(
    BANNER_COOKIE,
    {
      [campaignPath]: { seen, clicked },
    },
    getCookieConfig()
  );
};

const shouldShowBanner = (pathWithoutBasename, campaignPath, displayOnce) => {
  if (isBlacklistPath(pathWithoutBasename)) {
    return false;
  }

  if (!displayOnce) {
    return true;
  }

  if (!hasCookiePreferences) {
    return true;
  }

  const bannerData = getBannerCookie(campaignPath);
  if (!bannerData) {
    return false;
  }

  return bannerData.seen && !bannerData.clicked;
};

const CampaignBanner = ({ pathWithoutBasename, campaignPath, children, displayOnce }) => {
  const showBanner = shouldShowBanner(pathWithoutBasename, campaignPath, displayOnce);

  // Set initial cookie if banner should be shown and displayOnce is enabled
  if (showBanner && displayOnce && !getBannerCookie(campaignPath)) {
    setBannerCookie(campaignPath, true, false);
  }

  if (!showBanner) return null;

  const trackBannerClick = () => {
    if (typeof window !== 'undefined' && window.analytics) {
      window.analytics.track('Clicked CTA Button', {
        title: campaignPath,
        from: pathWithoutBasename,
      });
    }

    setBannerCookie(campaignPath, true, true);
  };

  return (
    <Banner to={`/${campaignPath}`} onClick={trackBannerClick}>
      <BannerText>{children}</BannerText>
      <BannerIcon src={arrowRightIcon} alt="" />
    </Banner>
  );
};

CampaignBanner.propTypes = {
  pathWithoutBasename: PropTypes.string.isRequired,
  campaignPath: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  displayOnce: PropTypes.bool,
};

export default withRouteData(CampaignBanner);



// WEBPACK FOOTER //
// /tmp/build_83f21ab6/src/components/main/header/CampaignBanner.js